import React, { useState } from "react";
import AccountSectionWrapper from "./AccountSectionWrapper";
import ModalNew from "../popup/Modal";
import ConfirmationModal from "../popup/ConfirmationModal";
import VerificationModal from "../popup/VerificationModal";
import ApiResponseModal from "../popup/ApiResponseModal";
import { PopupHeader, PopupContent } from "../popup/common";
import LoadingPlaceholder from "../loading/LoadingPlaceholder";
import PasskeyCard from "../cards/passkeys/PasskeyCard";
import {
  useListPasskeysQuery,
  useGetPasskeyRegistrationOptionsMutation,
  useVerifyPasskeyRegistrationMutation,
  useRevokePasskeyMutation,
} from "../../../api/account";
import {
  isPasskeyUiAvailable,
  isPasskeyCancelledError,
  transformRegistrationOptions,
  serializeRegistrationCredential,
  signalRevokedCredential,
} from "../../../utils/auth/webauthnHelpers";

/** Passkeys section for Account Settings -> Security. Wrapper-only gate. */
const PasskeysSection = () => {
  const passkeysAvailable = isPasskeyUiAvailable();

  const {
    data: passkeys,
    isLoading: isListLoading,
    isError: isListError,
    error: listError,
  } = useListPasskeysQuery(undefined, { skip: !passkeysAvailable });

  const [getRegistrationOptions] = useGetPasskeyRegistrationOptionsMutation();
  const [verifyRegistration] = useVerifyPasskeyRegistrationMutation();
  const [revokePasskey] = useRevokePasskeyMutation();

  // Add-passkey flow: name -> MFA (if required) -> WebAuthn ceremony -> verify
  const [isNameModalOpen, setIsNameModalOpen] = useState(false);
  const [friendlyName, setFriendlyName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isMfaModalOpen, setIsMfaModalOpen] = useState(false);
  const [mfaStep, setMfaStep] = useState("verification");
  const [mfaType, setMfaType] = useState("email");
  const [mfaErrors, setMfaErrors] = useState([]);

  // Revoke flow: confirm -> MFA (if required) -> DELETE
  const [passkeyToRevoke, setPasskeyToRevoke] = useState(null);
  const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false);
  const [isRevokeMfaOpen, setIsRevokeMfaOpen] = useState(false);
  const [revokeMfaStep, setRevokeMfaStep] = useState("verification");
  const [revokeMfaType, setRevokeMfaType] = useState("email");
  const [revokeMfaErrors, setRevokeMfaErrors] = useState([]);
  const [revokingId, setRevokingId] = useState(null);

  const [isResultModalOpen, setIsResultModalOpen] = useState(false);
  const [resultMessage, setResultMessage] = useState("");
  const [isResultSuccess, setIsResultSuccess] = useState(false);
  const [resultRetryAction, setResultRetryAction] = useState(null);

  if (!passkeysAvailable) {
    return null;
  }

  const showResult = (message, success, retryAction = null) => {
    setResultMessage(message);
    setIsResultSuccess(success);
    setResultRetryAction(retryAction);
    setIsResultModalOpen(true);
  };

  const handleStartAdd = () => {
    setFriendlyName("");
    setIsNameModalOpen(true);
  };

  const runWebAuthnRegistration = async (options) => {
    const credential = await navigator.credentials.create(
      transformRegistrationOptions(options)
    );
    return serializeRegistrationCredential(credential);
  };

  const requestOptionsAndCreate = async (mfaCode = null) => {
    const result = await getRegistrationOptions(mfaCode);

    if (result.error) {
      const { status, data } = result.error;

      if (status === 403 && data?.code === "PASSKEYS_NOT_ENABLED") {
        setIsMfaModalOpen(false);
        showResult("Passkeys are not available for your account yet.", false);
        return;
      }

      if (data?.code === "MFA_REQUIRED") {
        setMfaType(data.mfa_type || "email");
        setMfaStep("verification");
        setMfaErrors([]);
        setIsMfaModalOpen(true);
        return;
      }

      // Invalid/expired code: re-show the same MFA modal with the error.
      if (data?.code === "MFA_CODE_INVALID" && isMfaModalOpen) {
        setMfaErrors([data.detail || "Invalid or expired code."]);
        return;
      }

      setIsMfaModalOpen(false);
      showResult(
        data?.detail || "Could not start passkey setup. Please try again.",
        false,
        "add"
      );
      return;
    }

    setIsMfaModalOpen(false);

    try {
      const credential = await runWebAuthnRegistration(result.data);
      const verifyResult = await verifyRegistration({
        credential,
        friendly_name: friendlyName.trim(),
      });

      if (verifyResult.error) {
        showResult(
          verifyResult.error.data?.detail ||
            "Could not save the passkey. Please try again.",
          false,
          "add"
        );
        return;
      }

      showResult("Passkey added successfully.", true);
    } catch (error) {
      if (isPasskeyCancelledError(error)) {
        return;
      }
      showResult(
        "Could not complete the passkey prompt. Please try again.",
        false,
        "add"
      );
    }
  };

  const handleConfirmName = async () => {
    setIsNameModalOpen(false);
    setIsCreating(true);
    try {
      await requestOptionsAndCreate();
    } finally {
      setIsCreating(false);
    }
  };

  const handleMfaConfirm = async (code) => {
    setMfaErrors([]);
    await requestOptionsAndCreate(code);
  };

  const handleMfaClose = () => {
    setIsMfaModalOpen(false);
    setMfaErrors([]);
  };

  const handleStartRevoke = (passkey) => {
    setPasskeyToRevoke(passkey);
    setIsRevokeConfirmOpen(true);
  };

  const requestRevoke = async (mfaCode = null) => {
    setRevokingId(passkeyToRevoke.id);
    const result = await revokePasskey({
      id: passkeyToRevoke.id,
      mfa_code: mfaCode,
    });
    setRevokingId(null);

    if (result.error) {
      const { status, data } = result.error;

      if (status === 404) {
        setIsRevokeMfaOpen(false);
        showResult("This passkey could not be found.", false);
        setPasskeyToRevoke(null);
        return;
      }

      if (data?.code === "MFA_REQUIRED") {
        setRevokeMfaType(data.mfa_type || "email");
        setRevokeMfaStep("verification");
        setRevokeMfaErrors([]);
        setIsRevokeMfaOpen(true);
        return;
      }

      if (data?.code === "MFA_CODE_INVALID") {
        setRevokeMfaErrors([data.detail || "Invalid or expired code."]);
        setIsRevokeMfaOpen(true);
        return;
      }

      setIsRevokeMfaOpen(false);
      showResult(
        data?.detail || "Could not remove the passkey. Please try again.",
        false
      );
      setPasskeyToRevoke(null);
      return;
    }

    setIsRevokeMfaOpen(false);
    setPasskeyToRevoke(null);

    // Best-effort only: the server-side revocation already succeeded
    // regardless of whether the password manager signal is accepted.
    const signalAccepted = await signalRevokedCredential({
      rpId: result.data?.rp_id,
      credentialId: result.data?.revoked_credential_id,
    });

    showResult(
      signalAccepted
        ? "Passkey removed successfully."
        : "The passkey was removed from your Plaintr account. If you have trouble creating another one, remove the old copy from your device's password manager.",
      true
    );
  };

  const handleConfirmRevoke = async () => {
    setIsRevokeConfirmOpen(false);
    await requestRevoke();
  };

  const handleRevokeMfaConfirm = async (code) => {
    setRevokeMfaErrors([]);
    await requestRevoke(code);
  };

  const handleRevokeMfaClose = () => {
    setIsRevokeMfaOpen(false);
    setRevokeMfaErrors([]);
    setPasskeyToRevoke(null);
  };

  const handleCloseResultModal = () => {
    setIsResultModalOpen(false);
    setResultMessage("");
    setIsResultSuccess(false);
    setResultRetryAction(null);
  };

  const handleRetryResult = () => {
    const retryAction = resultRetryAction;
    handleCloseResultModal();

    if (retryAction === "add") {
      handleStartAdd();
    }
  };

  const passkeyList = passkeys || [];

  return (
    <>
      <AccountSectionWrapper
        title="Passkeys"
        buttonText="Add passkey"
        buttonIcon="key_white.svg"
        onButtonClick={handleStartAdd}
      >
        <div className="security-content">
          {isListLoading ? (
            <LoadingPlaceholder rows={1} type="card" />
          ) : isListError ? (
            listError?.status === 403 ? (
              <p style={{ color: "#64748b" }}>
                Passkeys are not available for your account yet.
              </p>
            ) : (
              <p style={{ color: "#64748b" }}>
                Could not load your passkeys. Please try again later.
              </p>
            )
          ) : passkeyList.length === 0 ? (
            <p style={{ color: "#64748b" }}>
              Use a passkey to sign in without typing a password, using your
              device's screen lock.
            </p>
          ) : (
            <div
              style={{ display: "flex", flexDirection: "column", gap: "12px" }}
            >
              {passkeyList.map((passkey) => (
                <PasskeyCard
                  key={passkey.id}
                  passkey={passkey}
                  onRevoke={handleStartRevoke}
                  isRevoking={revokingId === passkey.id}
                />
              ))}
            </div>
          )}
        </div>
      </AccountSectionWrapper>

      {/* Step 1: friendly name */}
      {isNameModalOpen && (
        <ModalNew className="content-modal">
          <PopupHeader
            title="Add a passkey"
            setOpenState={setIsNameModalOpen}
            icon={`${process.env.PUBLIC_URL}/images/icons/key_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <div className="modal-form">
              <div className="form-field">
                <label className="field-label">Name this passkey</label>
                <input
                  type="text"
                  className="form-input"
                  placeholder="e.g. My iPhone"
                  maxLength={100}
                  value={friendlyName}
                  onChange={(e) => setFriendlyName(e.target.value)}
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="cta-button primary short text-center"
                  onClick={handleConfirmName}
                  disabled={isCreating}
                >
                  Continue
                </button>
              </div>
            </div>
          </PopupContent>
        </ModalNew>
      )}

      {/* Step 2: MFA re-auth before issuing the registration challenge */}
      <VerificationModal
        isOpen={isMfaModalOpen}
        onClose={handleMfaClose}
        onConfirm={handleMfaConfirm}
        verificationType={mfaType}
        step={mfaStep}
        errors={mfaErrors}
        onDismissError={() => setMfaErrors([])}
        verificationTitle="Confirm it's you"
        confirmButtonText="Continue"
      />

      {/* Revoke: confirmation, then MFA */}
      <ConfirmationModal
        isOpen={isRevokeConfirmOpen}
        onClose={() => {
          setIsRevokeConfirmOpen(false);
          setPasskeyToRevoke(null);
        }}
        onConfirm={handleConfirmRevoke}
        title="Remove passkey"
        message={`Are you sure you want to remove "${
          passkeyToRevoke?.friendly_name || "this passkey"
        }"? You will no longer be able to sign in with it.`}
        confirmText="Remove"
        confirmButtonClass="primary"
      />

      <VerificationModal
        isOpen={isRevokeMfaOpen}
        onClose={handleRevokeMfaClose}
        onConfirm={handleRevokeMfaConfirm}
        verificationType={revokeMfaType}
        step={revokeMfaStep}
        errors={revokeMfaErrors}
        onDismissError={() => setRevokeMfaErrors([])}
        verificationTitle="Confirm it's you"
        confirmButtonText="Remove passkey"
      />

      <ApiResponseModal
        isOpen={isResultModalOpen}
        isLoading={false}
        isSuccess={isResultSuccess}
        message={resultMessage}
        onClose={handleCloseResultModal}
        onRetry={resultRetryAction === "add" ? handleRetryResult : undefined}
      />
    </>
  );
};

export default PasskeysSection;
